// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {getStatusTypeLabel} from '@app/features/app/constants/AppConstants';
import {useAnimatedMediaPlaybackAllowed} from '@app/features/app/hooks/useAnimatedMediaPlayback';
import {getAvatarStatusLayout} from '@app/features/ui/components/AvatarStatusLayout';
import styles from '@app/features/ui/components/BaseAvatar.module.css';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {StatusType} from '@fluxer/constants/src/StatusConstants';
import {normalizeStatus, StatusTypes} from '@fluxer/constants/src/StatusConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import React, {useCallback, useEffect, useId, useMemo, useState} from 'react';

const AVATAR_DESCRIPTOR = msg({
	message: 'Avatar',
	comment: 'Accessible label for an avatar image.',
});
const TYPING_INDICATOR_DESCRIPTOR = msg({
	message: 'Typing indicator',
	comment: 'Accessible label for the typing-indicator element.',
});
const STATUS_DESCRIPTOR = msg({
	message: '{effectiveStatusLabel} status',
	comment: 'Accessible label for the avatar status badge; placeholder is the localized status name.',
});

const TYPING_DOT_GAP_RATIO = 0.12;
const TYPING_DOT_SIZE_RATIO = 0.25;

type AvatarCSSProperties = React.CSSProperties & {
	'--avatar-status-width'?: string;
	'--avatar-status-height'?: string;
	'--avatar-status-right'?: string;
	'--avatar-status-bottom'?: string;
	'--avatar-status-radius'?: string;
	'--avatar-status-color'?: string;
	'--avatar-typing-dot-gap'?: string;
	'--avatar-typing-dot-size'?: string;
};

type AvatarMaskCSSProperties = React.CSSProperties & {
	'--avatar-mask-cx'?: string;
	'--avatar-mask-x'?: string;
	'--avatar-mask-width'?: string;
};

const SVG_MASK_URL_CACHE = new Map<string, string>();
const AVATAR_VIEW_BOX_CACHE = new Map<number, string>();
const STATIC_AVATAR_MASK_SIZES = new Set([16, 20, 24, 32, 36, 40, 44, 48, 56, 80, 120]);

type DynamicAvatarMaskKind = 'animated' | 'mobile' | 'round' | 'typing';

interface AvatarMaskConfig {
	id: string;
	dynamicKind: DynamicAvatarMaskKind | null;
}

function getSvgMaskUrl(maskId: string): string {
	if (maskId.startsWith('svg-mask-avatar-dynamic-')) {
		return `url(#${maskId})`;
	}
	const cached = SVG_MASK_URL_CACHE.get(maskId);
	if (cached) return cached;
	const value = `url(#${maskId})`;
	SVG_MASK_URL_CACHE.set(maskId, value);
	return value;
}

function getAvatarViewBox(size: number): string {
	const cached = AVATAR_VIEW_BOX_CACHE.get(size);
	if (cached) return cached;
	const value = `0 0 ${size} ${size}`;
	AVATAR_VIEW_BOX_CACHE.set(size, value);
	return value;
}

function getDynamicAvatarMaskId(kind: DynamicAvatarMaskKind, size: number): string {
	return `svg-mask-avatar-dynamic-${kind}-${String(size).replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

interface BaseAvatarProps {
	size: number;
	avatarUrl: string;
	hoverAvatarUrl?: string;
	status?: StatusType | string | null;
	shouldPlayAnimated?: boolean;
	forceAnimatedPlayback?: boolean;
	isTyping?: boolean;
	showOffline?: boolean;
	showSkeleton?: boolean;
	className?: string;
	isClickable?: boolean;
	userTag?: string;
	statusLabel?: string | null;
	disableStatusTooltip?: boolean;
	isMobileStatus?: boolean;
	animateStatusCutout?: boolean;
	title?: never;
	[themeAttr: `data-flx-${string}`]: string | undefined;
}

export const BaseAvatar = React.forwardRef<HTMLDivElement, BaseAvatarProps>(
	(
		{
			size,
			avatarUrl,
			hoverAvatarUrl,
			status,
			shouldPlayAnimated = false,
			forceAnimatedPlayback = false,
			isTyping = false,
			showOffline = true,
			showSkeleton = false,
			className,
			isClickable = false,
			userTag,
			statusLabel,
			disableStatusTooltip = false,
			isMobileStatus = false,
			animateStatusCutout = false,
			...props
		},
		ref,
	) => {
		const {i18n} = useLingui();
		const animatedMediaPlaybackEnabled = Boolean((shouldPlayAnimated || forceAnimatedPlayback) && hoverAvatarUrl);
		const animatedMediaPlaybackAllowed = useAnimatedMediaPlaybackAllowed({enabled: animatedMediaPlaybackEnabled});
		const normalizedStatus = status == null ? null : normalizeStatus(status);
		const renderableStatus = resolveRenderableStatus(normalizedStatus);
		const layout = getAvatarStatusLayout(size, isMobileStatus);
		const rawDynamicMaskId = useId();
		const dynamicAvatarMaskId = useMemo(
			() => `svg-mask-avatar-dynamic-${rawDynamicMaskId.replace(/[^A-Za-z0-9_-]/g, '_')}`,
			[rawDynamicMaskId],
		);
		const isMobileOnline = isMobileStatus && renderableStatus === StatusTypes.ONLINE && !isTyping;
		const shouldShowStatus =
			layout.supportsStatus &&
			(isTyping || (normalizedStatus != null && (showOffline || renderableStatus !== StatusTypes.OFFLINE)));
		const shouldAnimateAvatarMask = animateStatusCutout && shouldShowStatus && !isMobileOnline;
		const candidateUrl =
			animatedMediaPlaybackEnabled && animatedMediaPlaybackAllowed ? hoverAvatarUrl || '' : avatarUrl;
		const [imgError, setImgError] = useState(false);
		useEffect(() => {
			setImgError(false);
		}, [candidateUrl]);
		const handleImageError = useCallback(() => {
			setImgError(true);
		}, []);
		const showFallback = !candidateUrl || imgError;
		const avatarMask = resolveAvatarMask({
			shouldShowStatus,
			isTyping,
			isMobileOnline,
			size,
			animatedMaskId: shouldAnimateAvatarMask ? dynamicAvatarMaskId : null,
		});
		const avatarMaskUrl = getSvgMaskUrl(avatarMask.id);
		const statusMaskId = isMobileOnline
			? `svg-mask-status-online-mobile-${size}`
			: `svg-mask-status-${renderableStatus}`;
		const avatarViewBox = getAvatarViewBox(size);
		const statusColor = `var(--status-${renderableStatus})`;
		const containerStyle = useMemo<React.CSSProperties>(() => ({width: size, height: size}), [size]);
		const statusContainerStyle = useMemo<AvatarCSSProperties>(() => {
			const width = isTyping ? layout.innerTypingWidth : layout.innerStatusWidth;
			const height = isTyping ? layout.innerTypingHeight : layout.innerStatusHeight;
			const right = isTyping ? layout.innerTypingRight : layout.innerStatusRight;
			const bottom = isTyping ? layout.innerTypingBottom : layout.innerStatusBottom;
			const radius = isTyping ? layout.innerTypingHeight / 2 : isMobileOnline ? 0 : layout.innerStatusHeight / 2;
			const dotSize = Math.round(layout.innerTypingHeight * TYPING_DOT_SIZE_RATIO);
			const dotGap = Math.round(layout.innerTypingHeight * TYPING_DOT_GAP_RATIO);
			return {
				'--avatar-status-width': `${width}px`,
				'--avatar-status-height': `${height}px`,
				'--avatar-status-right': `${right}px`,
				'--avatar-status-bottom': `${bottom}px`,
				'--avatar-status-radius': `${radius}px`,
				'--avatar-status-color': statusColor,
				'--avatar-typing-dot-gap': `${dotGap}px`,
				'--avatar-typing-dot-size': `${dotSize}px`,
			};
		}, [isMobileOnline, isTyping, layout, statusColor]);
		const statusContainerClassName = `${styles.statusContainer} ${
			isTyping || disableStatusTooltip ? styles.statusTooltipDisabled : ''
		} ${Accessibility.useReducedMotion ? styles.reducedMotion : ''}`.trim();
		const ariaLabel = statusLabel && userTag ? `${userTag}, ${statusLabel}` : userTag || i18n._(AVATAR_DESCRIPTOR);
		const effectiveStatusLabel = statusLabel || (normalizedStatus ? getStatusTypeLabel(i18n, normalizedStatus) : '');
		const interactiveProps = useMemo(
			() =>
				isClickable
					? ({role: 'button', 'aria-label': ariaLabel, tabIndex: 0} as const)
					: ({'aria-hidden': true} as const),
			[ariaLabel, isClickable],
		);
		const statusBadge = shouldShowStatus ? (
			<div
				className={statusContainerClassName}
				style={statusContainerStyle}
				role="img"
				aria-label={isTyping ? i18n._(TYPING_INDICATOR_DESCRIPTOR) : i18n._(STATUS_DESCRIPTOR, {effectiveStatusLabel})}
				data-flx="ui.base-avatar.status-container"
			>
				{isTyping ? (
					<div className={styles.typingBubble} data-flx="ui.base-avatar.div--2">
						<div className={styles.typingDots} data-flx="ui.base-avatar.div--3">
							<div className={styles.typingDot} data-flx="ui.base-avatar.div--4" />
							<div className={styles.typingDot} data-flx="ui.base-avatar.div--5" />
							<div className={styles.typingDot} data-flx="ui.base-avatar.div--6" />
						</div>
					</div>
				) : (
					<StatusIndicatorSvg
						width={layout.innerStatusWidth}
						height={isMobileOnline ? layout.innerStatusHeight : layout.innerStatusWidth}
						statusColor={statusColor}
						statusMaskId={statusMaskId}
						data-flx="ui.base-avatar.status-indicator-svg"
					/>
				)}
			</div>
		) : null;
		const avatarElement = (
			<div
				ref={ref}
				className={`${styles.container} ${isClickable ? styles.clickable : ''} ${className || ''}`.trim()}
				style={containerStyle}
				data-flx="ui.base-avatar.div"
				{...interactiveProps}
				{...props}
			>
				<svg
					viewBox={avatarViewBox}
					className={styles.overlay}
					aria-hidden
					role="presentation"
					data-flx="ui.base-avatar.overlay"
				>
					{avatarMask.dynamicKind && (
						<DynamicAvatarMask
							id={avatarMask.id}
							kind={avatarMask.dynamicKind}
							size={size}
							layout={layout}
							isTyping={isTyping}
							reducedMotion={Accessibility.useReducedMotion}
						/>
					)}
					{showSkeleton ? (
						<rect
							x={0}
							y={0}
							width={size}
							height={size}
							mask={avatarMaskUrl}
							fill="var(--background-modifier-accent)"
							opacity={0.45}
							data-flx="ui.base-avatar.skeleton"
						/>
					) : showFallback ? null : (
						<image
							href={candidateUrl}
							width={size}
							height={size}
							mask={avatarMaskUrl}
							preserveAspectRatio="xMidYMid slice"
							onError={handleImageError}
							data-flx="ui.base-avatar.image"
						/>
					)}
					<rect
						className={styles.hoverOverlay}
						x={0}
						y={0}
						width={size}
						height={size}
						fill="black"
						mask={avatarMaskUrl}
						data-flx="ui.base-avatar.hover-overlay"
					/>
				</svg>
				{statusBadge &&
					(isTyping || disableStatusTooltip ? (
						statusBadge
					) : (
						<Tooltip text={effectiveStatusLabel} data-flx="ui.base-avatar.tooltip">
							{statusBadge}
						</Tooltip>
					))}
			</div>
		);

		if (!isClickable) {
			return avatarElement;
		}

		return (
			<FocusRing offset={-2} enabled data-flx="ui.base-avatar.focus-ring">
				{avatarElement}
			</FocusRing>
		);
	},
);

BaseAvatar.displayName = 'BaseAvatar';

const resolveRenderableStatus = (status: StatusType | null | undefined): StatusType => {
	if (status == null) return StatusTypes.OFFLINE;
	if (status === StatusTypes.INVISIBLE) return StatusTypes.OFFLINE;
	return status;
};
const resolveAvatarMask = ({
	shouldShowStatus,
	isTyping,
	isMobileOnline,
	size,
	animatedMaskId,
}: {
	shouldShowStatus: boolean;
	isTyping: boolean;
	isMobileOnline: boolean;
	size: number;
	animatedMaskId?: string | null;
}): AvatarMaskConfig => {
	if (!shouldShowStatus) return {id: 'svg-mask-avatar-default', dynamicKind: null};
	if (animatedMaskId && !isMobileOnline) {
		return {id: animatedMaskId, dynamicKind: 'animated'};
	}
	if (STATIC_AVATAR_MASK_SIZES.has(size)) {
		if (isTyping) return {id: `svg-mask-avatar-status-typing-${size}`, dynamicKind: null};
		if (isMobileOnline) return {id: `svg-mask-avatar-status-mobile-${size}`, dynamicKind: null};
		return {id: `svg-mask-avatar-status-round-${size}`, dynamicKind: null};
	}
	const dynamicKind = isTyping ? 'typing' : isMobileOnline ? 'mobile' : 'round';
	return {id: getDynamicAvatarMaskId(dynamicKind, size), dynamicKind};
};

interface DynamicAvatarMaskProps {
	id: string;
	kind: DynamicAvatarMaskKind;
	size: number;
	layout: ReturnType<typeof getAvatarStatusLayout>;
	isTyping?: boolean;
	reducedMotion?: boolean;
}

const DynamicAvatarMask = React.memo(function DynamicAvatarMask({
	id,
	kind,
	size,
	layout,
	isTyping = false,
	reducedMotion = false,
}: DynamicAvatarMaskProps) {
	const cutoutRadius = layout.cutoutRadius;
	const cutoutY = layout.cutoutCy - cutoutRadius;
	const typingExtension = Math.max(0, layout.innerTypingWidth - layout.innerStatusWidth);
	const typingBridgeShift = layout.innerStatusRight - layout.innerTypingRight;
	const typingLeftCx = layout.cutoutCx - typingExtension + typingBridgeShift;
	const typingRightCx = layout.cutoutCx + typingBridgeShift;
	const animatedRightCapStyle: AvatarMaskCSSProperties = {
		'--avatar-mask-cx': `${isTyping ? typingRightCx : layout.cutoutCx}px`,
	};
	const animatedBridgeStyle: AvatarMaskCSSProperties = {
		'--avatar-mask-x': `${isTyping ? typingLeftCx : layout.cutoutCx}px`,
		'--avatar-mask-width': `${isTyping ? typingExtension : 0}px`,
	};
	const animatedLeftCapStyle: AvatarMaskCSSProperties = {
		'--avatar-mask-cx': `${isTyping ? typingLeftCx : layout.cutoutCx}px`,
	};
	const animatedMaskClassName = `${styles.animatedMaskShape} ${
		reducedMotion ? styles.animatedMaskReducedMotion : ''
	}`.trim();
	return (
		<defs data-flx="ui.base-avatar.dynamic-avatar-mask.defs">
			<mask
				id={id}
				maskUnits="userSpaceOnUse"
				x={0}
				y={0}
				width={size}
				height={size}
				data-flx="ui.base-avatar.dynamic-avatar-mask.mask"
			>
				<circle
					fill="white"
					cx={size / 2}
					cy={size / 2}
					r={size / 2}
					data-flx="ui.base-avatar.dynamic-avatar-mask.base"
				/>
				{kind === 'animated' ? (
					<>
						<circle
							className={animatedMaskClassName}
							style={animatedRightCapStyle}
							fill="black"
							cy={layout.cutoutCy}
							r={cutoutRadius}
							data-flx="ui.base-avatar.dynamic-avatar-mask.animated-right"
						/>
						<rect
							className={animatedMaskClassName}
							style={animatedBridgeStyle}
							fill="black"
							y={cutoutY}
							height={cutoutRadius * 2}
							data-flx="ui.base-avatar.dynamic-avatar-mask.animated-bridge"
						/>
						<circle
							className={animatedMaskClassName}
							style={animatedLeftCapStyle}
							fill="black"
							cy={layout.cutoutCy}
							r={cutoutRadius}
							data-flx="ui.base-avatar.dynamic-avatar-mask.animated-left"
						/>
					</>
				) : kind === 'typing' ? (
					<>
						<circle
							fill="black"
							cx={typingRightCx}
							cy={layout.cutoutCy}
							r={cutoutRadius}
							data-flx="ui.base-avatar.dynamic-avatar-mask.typing-right"
						/>
						<rect
							fill="black"
							x={typingLeftCx}
							y={cutoutY}
							width={typingExtension}
							height={cutoutRadius * 2}
							data-flx="ui.base-avatar.dynamic-avatar-mask.typing-bridge"
						/>
						<circle
							fill="black"
							cx={typingLeftCx}
							cy={layout.cutoutCy}
							r={cutoutRadius}
							data-flx="ui.base-avatar.dynamic-avatar-mask.typing-left"
						/>
					</>
				) : kind === 'mobile' ? (
					<rect
						fill="black"
						x={layout.cutoutCx - layout.innerStatusWidth / 2}
						y={layout.cutoutCy - layout.innerStatusHeight / 2}
						width={layout.innerStatusWidth}
						height={layout.innerStatusHeight}
						rx={Math.max(1, layout.innerStatusWidth * 0.1)}
						ry={Math.max(1, layout.innerStatusWidth * 0.1)}
						data-flx="ui.base-avatar.dynamic-avatar-mask.mobile"
					/>
				) : (
					<circle
						fill="black"
						cx={layout.cutoutCx}
						cy={layout.cutoutCy}
						r={cutoutRadius}
						data-flx="ui.base-avatar.dynamic-avatar-mask.round"
					/>
				)}
			</mask>
		</defs>
	);
});

DynamicAvatarMask.displayName = 'DynamicAvatarMask';

interface StatusIndicatorSvgProps {
	width: number;
	height: number;
	statusColor: string;
	statusMaskId: string;
}

const StatusIndicatorSvg = React.memo(function StatusIndicatorSvg({
	width,
	height,
	statusColor,
	statusMaskId,
}: StatusIndicatorSvgProps) {
	return (
		<svg
			width={width}
			height={height}
			viewBox="0 0 1 1"
			preserveAspectRatio="none"
			aria-hidden
			data-flx="ui.base-avatar.status-indicator-svg.svg"
		>
			<rect
				x={0}
				y={0}
				width={1}
				height={1}
				fill={statusColor}
				mask={getSvgMaskUrl(statusMaskId)}
				data-flx="ui.base-avatar.status-indicator-svg.rect"
			/>
		</svg>
	);
});

StatusIndicatorSvg.displayName = 'StatusIndicatorSvg';
